// The office filing cabinet: general business documents that belong to no client —
// signed agreements, accounting paperwork, forms from the authorities.
//
// Client files live on the client card and inside their job's publications; these
// have no client to hang off, which is why they get their own screen (and their own
// table, and their own branch of the R2 key layout). Nothing here knows about jobs,
// retainers or money.
//
// Two views, never mixed: the live list, and the archive. An archived document is
// out of sight by definition — but it is still stored, still viewable, still
// downloadable, and one click from being restored.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import { useLookups } from '../../context/LookupsContext';
import { documentsApi } from '../../api';
import he from '../../i18n/he';
import { messageForError } from '../../i18n/errors';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import DataTable from '../../components/DataTable';
import DateText from '../../components/DateText';
import Button from '../../components/Button';
import IconButton from '../../components/IconButton';
import { FileActions } from '../../components/FileList';
import { TrashIcon } from '../../components/icons';
import FileRemoveModal from '../../components/FileRemoveModal';
import DocumentModal from './DocumentModal';

const ALL = '';

export default function Documents() {
  const lookups = useLookups();
  const [category, setCategory] = useState(ALL);
  const [archived, setArchived] = useState(false);
  const [editing, setEditing] = useState(null); // document row, {} for new, or null
  const [removing, setRemoving] = useState(null); // document row, or null

  const { data, loading, error, reload } = useAsync(
    () => documentsApi.list({ archived, category: category || null }),
    [archived, category]
  );

  const restore = async (row) => {
    try {
      await documentsApi.setArchived(row.id, false);
      reload();
    } catch (e) {
      window.alert(messageForError(e));
    }
  };

  const afterRemoval = () => { setRemoving(null); reload(); };

  const columns = [
    { key: 'title', label: he.documents.name, primary: true, render: (r) => (
      <>
        <div>{r.title}</div>
        <span className="doc-file-name" title={r.original_name}>{r.original_name}</span>
      </>
    ) },
    // The drawer, as a badge — the organising fact of this screen, so it reads at a
    // glance rather than as another line of text.
    { key: 'category', label: he.documents.category, render: (r) => (
      <span className="badge gray">{r.category_label}</span>
    ) },
    { key: 'doc_date', label: he.documents.date, render: (r) => <DateText value={r.doc_date} /> },
    { key: 'uploaded_by_partner_name', label: he.documents.uploadedBy, render: (r) => (
      archived
        // In the archive, who put it away is the more useful fact than who uploaded it.
        ? <span title={he.documents.archivedBy}>{r.archived_by_partner_name || '—'}</span>
        : <span>{r.uploaded_by_partner_name || '—'}</span>
    ) },
    { key: 'actions', label: he.common.actions, action: true, render: (r) => (
      <div className="row">
        {/* The same view/download actions every uploaded file in this app already
            has, reused rather than rebuilt — only the signed-URL source differs,
            because these files answer on the documents endpoint. */}
        <FileActions file={r} getUrl={documentsApi.downloadUrl}>
          {!archived && (
            <IconButton
              icon={<TrashIcon />}
              label={he.documents.remove}
              danger
              onClick={() => setRemoving(r)}
            />
          )}
        </FileActions>
        {archived
          ? <Button small variant="subtle" onClick={() => restore(r)}>{he.documents.restore}</Button>
          : <Button small variant="subtle" onClick={() => setEditing(r)}>{he.common.edit}</Button>}
      </div>
    ) },
  ];

  return (
    <>
      <PageHeader title={archived ? he.documents.archiveTitle : he.documents.title} spread>
        {/* Styled here rather than left as a bare <select>: ui.css gives form
            controls their box through a `.field`-scoped rule, and a header filter
            sits outside any field. See styles/documents.css. */}
        <div className="doc-filter">
          <label htmlFor="doc-category-filter">{he.documents.category}</label>
          <select
            id="doc-category-filter"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value={ALL}>{he.documents.allCategories}</option>
            {lookups.docCategoryOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="row">
          <Button variant="subtle" onClick={() => setArchived((a) => !a)}>
            {archived ? he.documents.showLive : he.documents.showArchive}
          </Button>
          {!archived && <Button onClick={() => setEditing({})}>{he.documents.add}</Button>}
        </div>
      </PageHeader>

      <div className="card">
        <AsyncBoundary loading={loading} error={error}>
          <DataTable
            columns={columns}
            rows={data}
            empty={{ text: archived ? he.empty.documentsArchive : he.empty.documents }}
          />
        </AsyncBoundary>
      </div>

      {editing && (
        <DocumentModal
          document={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}

      {/* The same two-choice dialog a retainer's signed agreement gets — archive or
          destroy — shared rather than copied, because the behaviour is the decision
          and only the wording differs. */}
      {removing && (
        <FileRemoveModal
          labels={he.documents}
          name={removing.title}
          subtitle={
            <>
              {removing.category_label}
              {removing.doc_date && <> · <DateText value={removing.doc_date} /></>}
            </>
          }
          fileName={removing.original_name}
          onClose={() => setRemoving(null)}
          onArchive={async () => { await documentsApi.setArchived(removing.id, true); afterRemoval(); }}
          onDelete={async () => { await documentsApi.remove(removing.id); afterRemoval(); }}
        />
      )}
    </>
  );
}
